import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ThirdPartyWithFilesResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetThirdPartyByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

export function mapTrpcThirdPartyToGraphQL(
  trpcData: ThirdPartyWithFilesResponseRow | undefined
): GetThirdPartyByIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    third_party: trpcData,
  };
}

export const useGetThirdPartyByIdTRPC = (id: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.thirdParty.getById.queryOptions({ thirdPartyId: id }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcThirdPartyToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
